import Big from 'big.js';

const decimal = value => new Big(value == null || value === '' ? 0 : value);
const rounded = (value, scale) => Number(value.round(scale, 1).toString());
const equal = (a, b) => decimal(a).eq(decimal(b));
export const hasPurchaseCost = item => item.p_cost != null && item.p_cost !== '';

export function purchaseFactor(item) {
	const unit = item.cost_purchase_unit == null ? item.purchase_unit_code : item.cost_purchase_unit;
	if (!unit) return null;
	if (unit !== 'CS') return 1;
	const packing = Number(item.cost_packing == null ? item.packing : item.cost_packing);
	return Number.isInteger(packing) && packing > 0 ? packing : null;
}

function snapshot(item) {
	if (item.cost_purchase_unit == null) item.cost_purchase_unit = item.purchase_unit_code;
	if (item.cost_unit == null) item.cost_unit = item.unit_code;
	if (item.cost_packing == null) item.cost_packing = purchaseFactor(item);
}

export function purchaseCost(item) {
	if (item._jc_price_edited && !hasPurchaseCost(item)) return null;
	const factor = purchaseFactor(item);
	return hasPurchaseCost(item) ? Number(item.p_cost) : factor ? rounded(decimal(item.unit_price).times(factor), 3) : null;
}

export function sellingPrice(item) {
	return hasPurchaseCost(item) && purchaseFactor(item) ? rounded(decimal(item.p_cost).div(purchaseFactor(item)), 6) : Number(item.unit_price || 0);
}

export function sellingPriceText(item) {
	return sellingPrice(item).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

export function setPurchaseCost(item, value) {
	// CurrencyInput also emits when mounted/reformatted. Keep untouched legacy rows legacy.
	const displayed = purchaseCost(item);
	if (value != null && value !== '' && displayed != null && equal(value, displayed)) return false;
	snapshot(item);
	item.p_cost = value;
	item._jc_price_edited = true;
	return true;
}

export function applySellingCost(item, value) {
	snapshot(item);
	item.p_cost = purchaseFactor(item) ? rounded(decimal(value).times(purchaseFactor(item)), 3) : null;
	item._jc_price_edited = true;
}

export function initializeJcItem(item, is_new = false) {
	for (const key of ['p_cost', 'cost_packing', 'cost_purchase_unit', 'cost_unit']) {
		if (item[key] === undefined) item[key] = null;
	}
	item._jc_price_edited = false;
	Object.defineProperty(item, '_jc_original', { value: { ...item }, configurable: true });
	if (is_new && !hasPurchaseCost(item)) applySellingCost(item, item.unit_price);
	return item;
}

// #3167: SBM history keeps quantities that do not divide into cartons as 2-decimal CS.
const isFractionalQuantity = value => value != null && value !== '' && Number.isFinite(Number(value)) && !Number.isInteger(Number(value));

// Quantity input precision. Only an existing row (row_key from the load) whose loaded value is fractional
// gets 2 decimals; it is fixed at load, so an edit to a whole number keeps it. New, Back Order and
// PO-remainder rows have no row_key in _jc_original and stay whole-number input as before.
export function jcQuantityPrecision(company_id, item, field, row_key) {
	const original = company_id === 'jc' && item ? item._jc_original : null;
	return original && original[row_key] != null && isFractionalQuantity(original[field]) ? 2 : 0;
}

// Readonly header total precision. The header baseline has no quantities, so the current line total decides.
// Bind it to the input's :key as well: CurrencyInput applies a new value before new options, so without a
// remount a total that turns fractional is first rounded by the old precision and emitted back to the model.
export function jcTotalPrecision(company_id, value) {
	return company_id === 'jc' && isFractionalQuantity(value) ? 2 : 0;
}

// Pre-filter for `| commaSeparated`: a jc fractional quantity becomes '1,234.56'; anything else passes unchanged.
export function jcQuantityDisplay(company_id, value) {
	return company_id === 'jc' && typeof value === 'number' && isFractionalQuantity(value) ? value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value;
}

export function initializeJcHeader(header, items) {
	Object.defineProperty(header, '_jc_original', {
		value: {
			total: decimal(header.total_amount),
			discount: decimal(header.discount_amount),
			received: decimal(header.received_amount),
			lines: items.reduce((sum, row) => sum.plus(decimal(row.amount)), decimal(0)),
			received_lines: items.reduce((sum, row) => sum.plus(decimal(row.received_amount)), decimal(0)),
		},
		configurable: true,
	});
}

export function validateJcPrices(items) {
	for (const row of items) {
		if ((row._jc_price_edited || hasPurchaseCost(row)) && (!(row.cost_purchase_unit || row.purchase_unit_code) || !(row.cost_unit || row.unit_code))) return 'Purchase and selling units are required.';
		if (row._jc_price_edited && !hasPurchaseCost(row)) return 'P.cost is required.';
		if (hasPurchaseCost(row) && !purchaseFactor(row)) return 'QTY/CTN must be greater than zero.';
	}
	return null;
}

export function recalculateJc(items, header, shipment = false) {
	let total = decimal(0),
		received = decimal(0),
		quantity = decimal(0),
		received_quantity = decimal(0);
	items.forEach(item => {
		const original = item._jc_original;
		const factor = purchaseFactor(item);
		const legacy_factor = Math.max(Number(item.packing) || 1, 1);
		if (hasPurchaseCost(item) && factor) {
			item.unit_price = rounded(decimal(item.p_cost).div(factor), 3);
			item.amount = rounded(decimal(item.p_cost).times(decimal(item.quantity)), 2);
			item.received_amount = rounded(decimal(item.p_cost).times(decimal(item.received_quantity)), 2);
		} else if (!item._jc_price_edited) {
			item.amount =
				original && equal(item.quantity, original.quantity) && equal(item.unit_price, original.unit_price)
					? original.amount
					: rounded(
							decimal(item.quantity)
								.times(decimal(item.unit_price))
								.times(legacy_factor),
							3,
					  );
			item.received_amount =
				original && equal(item.received_quantity, original.received_quantity) && equal(item.unit_price, original.unit_price)
					? original.received_amount
					: rounded(
							decimal(item.received_quantity)
								.times(decimal(item.unit_price))
								.times(legacy_factor),
							3,
					  );
		}
		item.fob_amount =
			original && equal(item.quantity, original.quantity) && equal(item.fob_price, original.fob_price)
				? original.fob_amount
				: rounded(
						decimal(item.quantity)
							.times(decimal(item.fob_price))
							.times(hasPurchaseCost(item) ? factor || 1 : legacy_factor),
						3,
				  );
		item.remain_quantity = rounded(decimal(item.quantity).minus(decimal(item.shipped_quantity)), 3);
		item.remain_amount = hasPurchaseCost(item)
			? rounded(decimal(item.remain_quantity).times(decimal(item.p_cost)), 2)
			: rounded(
					decimal(item.remain_quantity)
						.times(decimal(item.unit_price))
						.times(legacy_factor),
					3,
			  );
		total = total.plus(decimal(item.amount));
		received = received.plus(decimal(item.received_amount));
		quantity = quantity.plus(decimal(item.quantity));
		received_quantity = received_quantity.plus(decimal(item.received_quantity));
	});
	header.item_amount = Number(total);
	const original = header._jc_original;
	if (original) {
		total = total.plus(original.total).minus(original.lines);
		received = received.plus(original.received).minus(original.received_lines);
		if (!shipment) total = total.plus(original.discount);
	}
	if (!shipment) total = total.minus(decimal(header.discount_amount));
	header.total_amount = rounded(total, 2);
	header.total_quantity = Number(quantity);
	if (shipment) {
		header.received_amount = rounded(received, 2);
		header.received_quantity = Number(received_quantity);
	}
}

// Preserve the existing class price-difference rule in selling units, using each row's own factor.
export function propagatePurchaseCost(items, index, value) {
	const current = items[index];
	if (!setPurchaseCost(current, value)) return;
	const factor = purchaseFactor(current);
	if (!factor || !hasPurchaseCost(current)) return;
	const selling = decimal(value).div(factor);
	for (let i = index + 1; i < items.length; i++) {
		const row = items[i];
		if (row.class_code === current.class_code)
			applySellingCost(
				row,
				selling
					.plus(decimal(row.cost))
					.minus(decimal(current.cost))
					.toString(),
			);
	}
}

export function prepareJcPurchasePrint(data) {
	const order_totals = {},
		selling_totals = {};
	const sum = (totals, unit, value) => {
		totals[unit || '—'] = (totals[unit || '—'] || decimal(0)).plus(value);
	};
	const format = value => Number(value).toLocaleString('en-US', { maximumFractionDigits: 3 });
	data.purchaseItemList.forEach(item => {
		const purchase_unit = item.cost_purchase_unit || item.purchase_unit_code;
		const selling_unit = item.cost_unit || item.unit_code;
		const factor = purchaseFactor(item);
		item.print_ord_qty = `${format(item.quantity)} ${purchase_unit || ''}`;
		item.print_our_qty = factor ? `${format(decimal(item.quantity).times(factor))} ${selling_unit || ''}` : '—';
		item.print_s_price = sellingPriceText(item);
		item.print_p_cost = hasPurchaseCost(item) || factor ? purchaseCost(item).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 }) : '—';
		sum(order_totals, purchase_unit, decimal(item.quantity));
		if (factor) sum(selling_totals, selling_unit, decimal(item.quantity).times(factor));
	});
	data.purchase.print_order_total = Object.keys(order_totals)
		.sort()
		.map(unit => `${format(order_totals[unit])} ${unit}`)
		.join(' / ');
	data.purchase.print_our_total = Object.keys(selling_totals)
		.sort()
		.map(unit => `${format(selling_totals[unit])} ${unit}`)
		.join(' / ');
	return data;
}
